import { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import EventIcon from "@mui/icons-material/Event";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import RateReviewIcon from "@mui/icons-material/RateReview";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchEvents } from "@/store/eventsSlice";
import { useStatistics } from "@hooks/useStatistics";
import { StatMetricCard } from "@components/StatMetricCard";
import { PageScaffold } from "@components/PageScaffold";
import { StatAccent } from "@/constants";
import { joinTextParts } from "@/utils/text";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const { items: events, loading: eventsLoading } = useAppSelector(
    (s) => s.events,
  );
  const { stats } = useStatistics();

  useEffect(() => {
    dispatch(fetchEvents());
  }, [dispatch]);

  const na = t("common.notAvailable");

  const statCards = useMemo(
    () => [
      {
        label: t("dashboard.totalEvents"),
        value: stats?.totalEvents ?? na,
        icon: EventIcon,
        color: StatAccent.coral,
      },
      {
        label: t("dashboard.totalRestaurants"),
        value: stats?.totalRestaurants ?? na,
        icon: RestaurantIcon,
        color: StatAccent.green,
      },
      {
        label: t("dashboard.totalReviews"),
        value: stats?.totalReviews ?? na,
        icon: RateReviewIcon,
        color: StatAccent.blue,
      },
      {
        label: t("dashboard.avgRating"),
        value:
          stats?.averageRating != null ? stats.averageRating.toFixed(1) : na,
        icon: StarIcon,
        color: StatAccent.amber,
      },
    ],
    [stats, t, na],
  );

  return (
    <PageScaffold>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("dashboard.welcome")}
        {user?.displayName
          ? `, ${user.displayName}`
          : user?.email
            ? `, ${user.email.split("@")[0]}`
            : ""}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/events/new")}
        >
          {t("dashboard.createEvent")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestaurantIcon />}
          onClick={() => navigate("/restaurants/new")}
        >
          {t("dashboard.addRestaurant")}
        </Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid key={s.label} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatMetricCard
              icon={s.icon}
              color={s.color}
              label={s.label}
              value={s.value}
            />
          </Grid>
        ))}
      </Grid>
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("dashboard.recentActivity")}
          </Typography>
          {eventsLoading ? (
            <Skeleton variant="rounded" height={100} />
          ) : events.length === 0 ? (
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              {t("common.noData")}
            </Typography>
          ) : (
            <List disablePadding>
              {events.slice(0, 5).map((ev) => (
                <ListItem
                  key={ev.id}
                  disablePadding
                  sx={{ py: 1, cursor: "pointer" }}
                  onClick={() => navigate(`/events/${ev.id}`)}
                >
                  <ListItemText
                    primary={ev.name}
                    secondary={joinTextParts(
                      ev.restaurant?.name,
                      new Date(ev.startedAt).toLocaleDateString(),
                    )}
                    slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                  />
                  <Chip
                    label={
                      new Date(ev.startedAt) > new Date()
                        ? t("events.upcoming")
                        : t("events.completed")
                    }
                    size="small"
                    color={
                      new Date(ev.startedAt) > new Date()
                        ? "primary"
                        : "default"
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </PageScaffold>
  );
}
